import type { RoleEnum, TrainerCapability } from '@zeus/index'

/**
 * Qui ouvre quel écran : les rôles admis par route protégée, et pour un
 * formateur la capacité exigée (#11).
 *
 * ⚠️ Source unique. `router.tsx` en tire ses loaders, et l'e2e
 * (`apps/e2e/tests/routes.spec.ts`) en tire sa matrice route × rôle : une
 * route ajoutée ici est testée pour chaque rôle sans écrire de test (#180).
 * C'est aussi pourquoi le fichier n'importe que des types — Playwright le lit
 * hors de l'app, sans le client GraphQL généré.
 *
 * Les clés sont les chemins relatifs à l'organisation (`/o/:slug/…`).
 */
type Role = `${RoleEnum}`
type Capability = `${TrainerCapability}`

export type RouteAccess = {
  roles: readonly Role[]
  capability?: Capability
  /**
   * L'écran ne s'ouvre à l'admin que si la plateforme a ouvert la facturation
   * en libre-service (`BILLING_SELF_SERVICE`, #165) ; sinon la page le renvoie
   * au tableau de bord. Le SuperAdmin n'est pas concerné.
   */
  selfServiceBilling?: true
}

const LEARNER_SPACE: readonly Role[] = ['Admin', 'Formateur', 'User']
const BACK_OFFICE: readonly Role[] = ['Admin', 'Formateur']

export const ROUTE_ACCESS = {
  // « Mon compte » et centre d'aide côté apprenant (#77, #78), à l'apprenant seul :
  // le personnel a les siens dans le back-office, et s'égarait ici sans chemin de
  // retour. Il est renvoyé sur son accueil (`getDefaultRoute`).
  account: { roles: ['User'] },
  help: { roles: ['User'] },
  // Démonstrations du centre d'aide (#105).
  'help/demo/path': { roles: LEARNER_SPACE },
  'help/demo/player': { roles: LEARNER_SPACE },
  // Mêmes droits que la vraie prévisualisation.
  'help/demo/preview': { roles: BACK_OFFICE, capability: 'MODULES_VIEW' },
  'my-sessions': { roles: LEARNER_SPACE },
  'my-sessions/:sessionId': { roles: LEARNER_SPACE },
  'scorm/preview/:moduleId': { roles: BACK_OFFICE, capability: 'MODULES_VIEW' },
  'scorm/:sessionId': { roles: LEARNER_SPACE },
  'scorm/:sessionId/:moduleId': { roles: LEARNER_SPACE },

  'back-office/dashboard': { roles: BACK_OFFICE },
  'back-office/questions': { roles: BACK_OFFICE, capability: 'STATISTICS_VIEW' },
  'back-office/reports': { roles: BACK_OFFICE, capability: 'STATISTICS_EXPORT' },
  'back-office/account': { roles: ['Admin', 'Formateur', 'SuperAdmin'] },
  'back-office/help': { roles: ['Admin', 'Formateur', 'SuperAdmin'] },
  // Démonstration de la fiche session (#243) : mêmes droits que la vraie fiche.
  'back-office/help/demo/session': { roles: BACK_OFFICE, capability: 'SESSIONS_VIEW' },
  'back-office/modules': { roles: BACK_OFFICE, capability: 'MODULES_VIEW' },
  'back-office/sessions': { roles: BACK_OFFICE, capability: 'SESSIONS_VIEW' },
  'back-office/sessions/:sessionId': { roles: BACK_OFFICE, capability: 'SESSIONS_VIEW' },
  'back-office/users': { roles: BACK_OFFICE, capability: 'LEARNERS_VIEW' },
  // Fiche apprenant (#242) : le détail de la liste, mêmes droits.
  'back-office/users/:userId': { roles: BACK_OFFICE, capability: 'LEARNERS_VIEW' },
  // Sa démonstration, à adresse fixe pour la visite guidée (#105, #242).
  'back-office/help/demo/learner': { roles: BACK_OFFICE, capability: 'LEARNERS_VIEW' },
  'back-office/groups': { roles: ['Admin'] },
  'back-office/certificates': { roles: ['Admin'] },
  // Questionnaire de fin de parcours (#320) : un choix d'admin.
  'back-office/survey': { roles: ['Admin'] },
  'back-office/reminders': { roles: ['Admin'] },
  // Registre des réclamations (#325) : un signalement ne doit jamais atteindre un formateur.
  'back-office/complaints': { roles: ['Admin'] },
  // Besoins d'aménagement (#327) : une donnée de santé, jamais montrée aux formateurs.
  'back-office/accommodations': { roles: ['Admin'] },
  // Amélioration continue (#328) : elle reprend réclamations et abandons, que le formateur ne voit pas.
  'back-office/quality': { roles: ['Admin'] },
  // Documents remis aux apprenants (#326) : leur dépôt et leurs accusés de lecture.
  'back-office/documents': { roles: ['Admin'] },
  'back-office/settings': { roles: ['Admin'] },
  'back-office/sso': { roles: ['Admin'] },
  'back-office/webhooks': { roles: ['Admin'] },
  'back-office/trainer-capabilities': { roles: ['Admin'] },
  'back-office/organizations': { roles: ['SuperAdmin'] },
  'back-office/trial-requests': { roles: ['SuperAdmin'] },
  'back-office/billing': { roles: ['SuperAdmin', 'Admin'], selfServiceBilling: true },
} as const satisfies Record<string, RouteAccess>

export type ProtectedPath = keyof typeof ROUTE_ACCESS
